import { useState } from 'react';
import { testimonials } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pj6npn2trhb35gvgk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91次元',
    url: 'https://91acgn.com',
    description: '专为二次元爱好者打造的内容聚合平台，汇聚动漫、漫画、轻小说、Cosplay等多元次元文化。',
    email: 'contact@91acgn.com',
    foundingDate: '2024',
    sameAs: [
      'https://weibo.com/91acgn',
      'https://space.bilibili.com/91acgn',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '91次元是什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '91次元是专为二次元爱好者打造的内容聚合平台，汇聚动漫番剧资讯、漫画推荐、轻小说阅读和Cosplay作品分享等多元次元文化内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在91次元发布文章？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '目前91次元的内容由专业编辑团队策划和创作。我们欢迎二次元爱好者通过联系表单与我们沟通内容合作和投稿意向。',
        },
      },
      {
        '@type': 'Question',
        name: '91次元的内容更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '91次元每日更新最新的二次元内容，包括每日番剧资讯、每周漫画推荐、轻小说书评以及定期的Cosplay作品展示。确保你永远不会错过任何精彩内容！',
        },
      },
      {
        '@type': 'Question',
        name: '如何加入91次元的社区交流？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过关注我们的微博、B站账号来参与社区讨论。同时，每篇文章底部都有评论区，欢迎与同好交流你的想法和观点。',
        },
      },
    ],
  };

  return (
    <>
      <title>关于我们 - 91次元 | 品牌介绍 · 口碑见证 · 联系我们</title>
      <meta name="description" content="了解91次元——专为二次元爱好者打造的内容聚合平台。查看品牌故事、用户口碑，以及如何联系我们。在91次元，开启你的次元之旅。" />
      <meta name="keywords" content="91次元关于我们,91次元品牌,91次元联系,二次元平台介绍" />
      <link rel="canonical" href="https://91acgn.com/about" />
      <meta property="og:title" content="关于我们 - 91次元" />
      <meta property="og:description" content="了解91次元——专为二次元爱好者打造的内容聚合平台。查看品牌故事、用户口碑，以及如何联系我们。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://91acgn.com/about" />
      <meta property="og:site_name" content="91次元" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="关于我们 - 91次元" />
      <meta name="twitter:description" content="了解91次元——专为二次元爱好者打造的内容聚合平台。" />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <main className="pt-20 pb-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="mt-6 mb-14">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">关于 91次元</h1>
            <p className="text-lg text-foreground-600 max-w-[700px] leading-relaxed">
              我们是一个为所有热爱二次元文化的人而生的平台。在这里，动漫、漫画、轻小说、Cosplay——每一种次元热爱都能找到归属。
            </p>
          </section>

          {/* Brand Story */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">品牌故事</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  <strong className="text-foreground-900 font-semibold">91次元</strong>诞生于2024年，由一群热爱二次元文化的年轻人共同创立。我们深知，对于每一个真正的二次元爱好者来说，ACG不只是一个标签，它是一种生活方式、一种精神寄托。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  平台名称中的"91"象征着"就要"——<strong className="text-foreground-900 font-semibold">就要次元，就要热爱</strong>。我们希望91次元能成为连接每一位二次元爱好者的桥梁，让每个人都能在这里找到志同道合的伙伴。
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  从最初的几个人小团队起步，到如今拥有<strong className="text-foreground-900 font-semibold">超过12万注册用户</strong>的二次元内容聚合平台，91次元的每一步成长都离不开每一位同好的支持与陪伴。
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">我们的使命</h3>
                <ul className="flex flex-col gap-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0 mt-0.5">
                      <i className="ri-heart-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-900 text-sm">汇聚次元热爱</h4>
                      <p className="text-sm text-foreground-500 mt-0.5">打造最全面的二次元内容平台，让每一种热爱都被看见。</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                      <i className="ri-group-line text-accent-500"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-900 text-sm">连接同好伙伴</h4>
                      <p className="text-sm text-foreground-500 mt-0.5">建立温暖友好的二次元社区，让每个人都不再孤单。</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-line text-secondary-500"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-900 text-sm">传播二次元文化</h4>
                      <p className="text-sm text-foreground-500 mt-0.5">用专业的内容和真诚的态度，让更多人了解和爱上二次元。</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-100 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-secondary-500"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">常见问题</h2>
            <div className="max-w-[800px] flex flex-col gap-3">
              <details className="group bg-background-100 rounded-lg overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between font-medium text-foreground-900 text-sm">
                  91次元是什么样的平台？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  91次元是专为二次元爱好者打造的内容聚合平台，汇聚动漫番剧资讯、漫画推荐、轻小说阅读和Cosplay作品分享等多元次元文化内容。
                </p>
              </details>
              <details className="group bg-background-100 rounded-lg overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between font-medium text-foreground-900 text-sm">
                  如何在91次元发布文章？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  目前91次元的内容由专业编辑团队策划和创作。我们欢迎二次元爱好者通过联系表单与我们沟通内容合作和投稿意向。
                </p>
              </details>
              <details className="group bg-background-100 rounded-lg overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between font-medium text-foreground-900 text-sm">
                  91次元的内容更新频率是怎样的？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  91次元每日更新最新的二次元内容，包括每日番剧资讯、每周漫画推荐、轻小说书评以及定期的Cosplay作品展示。确保你永远不会错过任何精彩内容！
                </p>
              </details>
              <details className="group bg-background-100 rounded-lg overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between font-medium text-foreground-900 text-sm">
                  如何加入91次元的社区交流？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  你可以通过关注我们的微博、B站账号来参与社区讨论。同时，每篇文章底部都有评论区，欢迎与同好交流你的想法和观点。
                </p>
              </details>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="max-w-[600px]">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">联系我们</h2>
            <p className="text-foreground-500 text-sm mb-6">有任何问题或建议？欢迎给我们留言，我们会在24小时内回复。</p>

            {formStatus === 'success' ? (
              <div className="bg-background-100 rounded-lg p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">发送成功！</h3>
                <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复你。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                    placeholder="请输入你的昵称"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                    placeholder="请输入你的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-colors resize-none"
                    placeholder="请描述你的问题或建议（500字以内）"
                  />
                </div>
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
                <button
                  type="submit"
                  className="px-8 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer self-start"
                >
                  发送留言
                </button>
              </form>
            )}
          </section>
        </div>
      </main>
    </>
  );
}